import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/App";
import { copy, menuData } from "@/i18n";

const TABS = ["pranzo", "sera", "vini", "cocktails", "birre", "spirits", "analcolici"];

const txt = (v, lang) => (typeof v === "string" ? v : v?.[lang] ?? v?.it ?? "");

const MenuSection = () => {
    const { lang } = useLang();
    const c = copy[lang].menu;
    const [tab, setTab] = useState("sera");

    return (
        <section id="menu" data-testid="menu-section" className="relative py-20 lg:py-32 bg-coal/40 border-y border-white/5">
            <div className="max-w-6xl mx-auto px-4 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center"
                >
                    <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-crimson">
                        {c.label}
                    </span>
                    <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl">
                        {c.title}
                    </h2>
                    <p className="mt-4 text-neutral-400 max-w-xl mx-auto text-sm sm:text-base">{c.subtitle}</p>
                </motion.div>

                <div data-testid="menu-tabs" className="mt-10 flex flex-wrap justify-center gap-2">
                    {TABS.map((t) => (
                        <button
                            key={t}
                            data-testid={`menu-tab-${t}`}
                            onClick={() => setTab(t)}
                            className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors border ${
                                tab === t
                                    ? "bg-crimson border-crimson text-white"
                                    : "border-white/15 text-neutral-400 hover:text-white hover:border-white/40"
                            }`}
                        >
                            {c.tabs[t]}
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={tab + lang}
                        data-testid="menu-list"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.35 }}
                        className="mt-12"
                    >
                        {menuData[tab].intro && (
                            <p className="mb-10 text-center font-serif italic text-lg sm:text-xl text-brass max-w-2xl mx-auto">
                                {menuData[tab].intro[lang] ?? menuData[tab].intro.it}
                            </p>
                        )}
                        {menuData[tab].video && (
                            <div className="mb-10 mx-auto max-w-3xl rounded-2xl overflow-hidden border border-white/10 glow-red">
                                <video
                                    key={menuData[tab].video}
                                    data-testid="menu-tab-video"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                    poster={`/media/${menuData[tab].video === "coravin" ? "coravin" : "social-spot"}-poster.jpg`}
                                    className="w-full h-auto block"
                                >
                                    <source src={`/media/${menuData[tab].video === "coravin" ? "coravin" : "social-spot"}.webm`} type="video/webm" />
                                    <source src={`/media/${menuData[tab].video === "coravin" ? "coravin" : "social-spot"}.mp4`} type="video/mp4" />
                                </video>
                            </div>
                        )}
                        <div className="sm:columns-2 gap-10">
                        {menuData[tab].groups.map((group, gi) => (
                            <div key={gi} data-testid={`menu-group-${tab}-${gi}`} className="break-inside-avoid mb-10">
                                <h4 className="font-serif italic text-xl sm:text-2xl text-brass border-b border-brass/25 pb-2 mb-1">
                                    {txt(group.group, lang)}
                                </h4>
                                {group.note && (
                                    <p className="text-[11px] font-mono text-neutral-500 mt-2 mb-1">{txt(group.note, lang)}</p>
                                )}
                                {group.items.map((item, i) => (
                                    <div
                                        key={i}
                                        data-testid={`menu-item-${tab}-${gi}-${i}`}
                                        className="group rounded-xl px-3 py-3 hover:bg-white/[0.04] transition-colors"
                                    >
                                        <div className="flex items-baseline gap-3">
                                            <h3 className="font-heading font-bold text-sm sm:text-base group-hover:text-crimson-bright transition-colors">
                                                {txt(item.name, lang)}
                                            </h3>
                                            {item.tag && (
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-crimson border border-crimson/40 rounded-full px-2 py-0.5">
                                                    {item.tag}
                                                </span>
                                            )}
                                            <span className="flex-1 border-b border-dotted border-white/20 translate-y-[-4px]" />
                                            {item.price != null && (
                                                <span className="font-mono text-crimson-bright font-semibold text-sm whitespace-nowrap">
                                                    {typeof item.price === "number" ? `${item.price}.–` : item.price}
                                                </span>
                                            )}
                                        </div>
                                        {item.desc && (
                                            <p className="mt-1 text-[13px] text-neutral-400 leading-relaxed normal-case">
                                                {txt(item.desc, lang)}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                        </div>
                    </motion.div>
                </AnimatePresence>

                <p className="mt-6 text-center text-xs text-neutral-500 font-mono tracking-wide">{c.note}</p>
            </div>
        </section>
    );
};

export default MenuSection;
